/**
 * ThemeCustomizationPanel — T97P2
 *
 * Controls for font family, accent color, text size, and border radius.
 * All changes apply in real-time and auto-save via useThemeCustomization.
 */
import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useThemeCustomization,
  FONT_OPTIONS,
  ACCENT_PRESETS,
  TEXT_SIZE_OPTIONS,
  BORDER_RADIUS_OPTIONS,
} from '@/hooks/useThemeCustomization';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

export function ThemeCustomizationPanel() {
  const { config, updateConfig } = useThemeCustomization();
  const [showCustomColor, setShowCustomColor] = useState(false);
  const [customHex, setCustomHex] = useState('');

  const isPresetSelected = ACCENT_PRESETS.some(p => p.hex === config.accentColor);

  const handleCustomColorApply = () => {
    const hex = customHex.startsWith('#') ? customHex : `#${customHex}`;
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
      updateConfig({ accentColor: hex });
      setShowCustomColor(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Font Family */}
      <div className="space-y-2">
        <Label>Font Family</Label>
        <Select
          value={config.fontFamily}
          onValueChange={(v) => updateConfig({ fontFamily: v })}
        >
          <SelectTrigger data-testid="font-family-select">
            <SelectValue placeholder="Select font" />
          </SelectTrigger>
          <SelectContent>
            {FONT_OPTIONS.map(f => (
              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Accent Color */}
      <div className="space-y-2">
        <Label>Accent Color</Label>
        <div className="flex flex-wrap gap-2 items-center">
          {ACCENT_PRESETS.map(preset => (
            <button
              key={preset.hex}
              type="button"
              title={preset.name}
              className={cn(
                'h-6 w-6 rounded-full transition-all shrink-0',
                config.accentColor === preset.hex
                  ? 'ring-2 ring-offset-2 ring-offset-background ring-foreground scale-110'
                  : 'hover:scale-110',
              )}
              style={{ backgroundColor: preset.hex }}
              onClick={() => {
                updateConfig({ accentColor: preset.hex });
                setShowCustomColor(false);
              }}
              data-testid={`accent-${preset.name.toLowerCase()}`}
            >
              {config.accentColor === preset.hex && (
                <Check className="h-3 w-3 text-white mx-auto" />
              )}
            </button>
          ))}
          <Button
            variant={!isPresetSelected && !showCustomColor ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowCustomColor(v => !v)}
            className="text-xs"
          >
            Custom
          </Button>
        </div>
        {showCustomColor && (
          <div className="flex gap-2 items-center mt-2">
            <Input
              placeholder="#ff5733"
              value={customHex}
              onChange={(e) => setCustomHex(e.target.value)}
              className="w-32"
              maxLength={7}
              data-testid="custom-color-input"
            />
            <Button size="sm" onClick={handleCustomColorApply}>
              Apply
            </Button>
            {!isPresetSelected && config.accentColor !== '#3b82f6' && (
              <div
                className="h-6 w-6 rounded-full border shrink-0"
                style={{ backgroundColor: config.accentColor }}
                title={`Current: ${config.accentColor}`}
              />
            )}
          </div>
        )}
      </div>

      <Separator />

      {/* Text Size */}
      <div className="space-y-2">
        <Label>Text Size</Label>
        <div className="flex gap-1" data-testid="text-size-toggle">
          {TEXT_SIZE_OPTIONS.map(opt => (
            <Button
              key={opt.value}
              variant={config.textSize === opt.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateConfig({ textSize: opt.value })}
              className="min-w-[44px]"
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      <Separator />

      {/* Border Radius */}
      <div className="space-y-2">
        <Label>Border Radius</Label>
        <div className="flex gap-1" data-testid="border-radius-toggle">
          {BORDER_RADIUS_OPTIONS.map(opt => (
            <Button
              key={opt.value}
              variant={config.borderRadius === opt.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateConfig({ borderRadius: opt.value })}
              className="min-w-[44px]"
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
